// src/components/Layout.js (Updated with active state enhancements)

import React, { useState, useEffect } from 'react';
import {
    AppBar,
    Box,
    Drawer,
    IconButton,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Toolbar,
    Typography,
    useTheme,
    Avatar,
    Menu,
    MenuItem,
    Divider,
    Tooltip,
    Collapse,
    Chip,
    CircularProgress,
} from '@mui/material';
import {
    Menu as MenuIcon,
    Dashboard,
    People,
    Restaurant,
    Receipt,
    QrCodeScanner,
    LunchDining,
    ChevronLeft as ChevronLeftIcon,
    ChevronRight as ChevronRightIcon,
    Logout,
    Person,
    ExpandMore,
    ExpandLess,
    Apartment,
    Home,
    Build,
    BusinessCenter,
    AttachMoney,
    Payment,
    Assessment,
    Settings,
    AdminPanelSettings,
    LocationCity,
    ManageAccounts,
    Security,
    ReportProblem,
    ReceiptLong,
    Paid,
    TrendingUp,
    Checklist,
    Assignment,
    EditNote,
    HomeWork,
    Key,
    Warning,
    Analytics,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { authService } from '../apiServices/authService';
import { companyService } from '../apiServices/companyService';
import { companyInfo as defaultCompanyInfo } from '../utils/dataInfo';

const drawerWidth = 280;
const collapsedDrawerWidth = 80;

const menuItems = [
    { 
        text: 'Dashboard', 
        icon: <Dashboard />, 
        path: '/dashboard',
        description: 'Overview & Analytics'
    },
    { 
        text: 'Properties',
        icon: <Apartment />, 
        description: 'Property Management',
        children: [
            { text: 'Property', icon: <HomeWork />, path: '/properties', description: 'Manage properties' },
            { text: 'Floor Unit', icon: <Home />, path: '/floor-units', description: 'Floor unit management' },
            { text: 'Maintenance Requests', icon: <Build />, path: '/maintenance', description: 'Track maintenance' },
            { text: 'Vacancy Management', icon: <Key />, path: '/vacancy', description: 'Manage vacancies' },
        ]
    },
    { 
        text: 'Tenants', 
        icon: <People />, 
        description: 'Tenant Management',
        children: [
            { text: 'Tenant', icon: <Person />, path: '/tenants', description: 'Manage tenants' },
            { text: 'Tenant Ledger', icon: <ReceiptLong />, path: '/tenant-ledger', description: 'Tenant financial records' },
            { text: 'Assign Tenant', icon: <Assignment />, path: '/assign-tenant', description: 'Assign tenants to units' },
            { text: 'Move-Out Checklist', icon: <Checklist />, path: '/move-out', description: 'Move-out procedures' },
        ]
    },
    { 
        text: 'Rent', 
        icon: <Paid />, 
        description: 'Rent Management',
        children: [
            { text: 'Rent Reminders', icon: <Warning />, path: '/rent-reminders', description: 'Rent reminders' },
        ]
    },
    { 
        text: 'Payments', 
        icon: <Payment />, 
        description: 'Payment Management',
        children: [
            { text: 'Security Deposit', icon: <Security />, path: '/security-deposits', description: 'Manage deposits' },
            { text: 'Payment History', icon: <Receipt />, path: '/payment-history', description: 'View payment records' },
            { text: 'Payment Receipts', icon: <ReceiptLong />, path: '/payment-receipts', description: 'Generate receipts' },
        ]
    },
    { 
        text: 'Expenses', 
        icon: <AttachMoney />, 
        description: 'Expense Tracking',
        children: [
            { text: 'Expense', icon: <Assessment />, path: '/expenses', description: 'Track expenses' },
            { text: 'Categories', icon: <BusinessCenter />, path: '/expense-categories', description: 'Manage categories' },
        ]
    },
    { 
        text: 'Reports', 
        icon: <Assessment />, 
        description: 'Analytics & Reports',
        children: [
            { text: 'Report Page', icon: <Assessment />, path: '/reports', description: 'Generate reports' },
        ]
    },
    { 
        text: 'Settings', 
        icon: <Settings />, 
        description: 'System Configuration',
        children: [
            { text: 'Company', icon: <LocationCity />, path: '/company', description: 'Company settings' },
            { text: 'Branch', icon: <BusinessCenter />, path: '/branch', description: 'Manage branches' },
            { text: 'User', icon: <ManageAccounts />, path: '/users', description: 'Manage users' },
            { text: 'Permission', icon: <AdminPanelSettings />, path: '/permission', description: 'User permissions' },
        ]
    },
];

const Layout = ({ children }) => {
    const [mobileOpen, setMobileOpen] = useState(false);
    const [collapsed, setCollapsed] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [userName, setUserName] = useState('');
    const [userEmail, setUserEmail] = useState('');
    const [openMenus, setOpenMenus] = useState({});
    const theme = useTheme();
    const navigate = useNavigate();
    const location = useLocation();

    // Company info state
    const [companyInfo, setCompanyInfo] = useState({
        name: defaultCompanyInfo.name || 'RCMS',
        logo_url: defaultCompanyInfo.logo || null,
        has_logo: false,
        code: null,
        currency: 'UGX'
    });
    const [loadingCompany, setLoadingCompany] = useState(true);
    const [logoError, setLogoError] = useState(false);

    // Get primary color from theme
    const primaryColor = theme.palette.primary?.main || '#1976d2';
    const primaryLight = theme.palette.primary?.light || '#42a5f5';
    const primaryActiveHover = theme.palette.primary?.dark || '#1565c0';

    useEffect(() => {
        const user = authService.getCurrentUser();
        if (user) {
            setUserName(user.name || user.username || 'User');
            setUserEmail(user.email || '');
        }
    }, []);

    // Sync active menu open state and close inactive parent menus
    useEffect(() => {
        const currentPath = location.pathname;
        const newOpenMenus = {};

        menuItems.forEach(item => {
            if (item.children) {
                const hasActiveChild = item.children.some(child => {
                    if (!child.path) return false;
                    return currentPath === child.path || currentPath.startsWith(child.path + '/');
                });

                if (hasActiveChild) {
                    newOpenMenus[item.text] = true;
                }
            }
        });

        setOpenMenus(newOpenMenus);
    }, [location.pathname]);

    // Fetch company info
    useEffect(() => {
        const fetchCompanyInfo = async () => {
            setLoadingCompany(true);
            try {
                const branchId = localStorage.getItem('branch_id') || null;
                const response = await companyService.getLoginInfo(branchId);
                
                if (response.status === 'success' && response.data) {
                    setCompanyInfo({
                        name: response.data.name || defaultCompanyInfo.name || 'RCMS',
                        logo_url: response.data.logo_url || defaultCompanyInfo.logo || null,
                        has_logo: response.data.has_logo || false,
                        code: response.data.code,
                        currency: response.data.currency || 'UGX'
                    });
                } else {
                    setCompanyInfo({
                        name: defaultCompanyInfo.name || 'RCMS',
                        logo_url: defaultCompanyInfo.logo || null,
                        has_logo: !!defaultCompanyInfo.logo,
                        code: null,
                        currency: 'UGX'
                    });
                }
            } catch (error) {
                console.error('Failed to fetch company info:', error);
                setCompanyInfo({
                    name: defaultCompanyInfo.name || 'RCMS',
                    logo_url: defaultCompanyInfo.logo || null,
                    has_logo: !!defaultCompanyInfo.logo,
                    code: null,
                    currency: 'UGX'
                });
            } finally {
                setLoadingCompany(false);
            }
        };
        
        fetchCompanyInfo();
    }, []);

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const handleToggleCollapse = () => {
        setCollapsed(!collapsed);
    };

    const handleMenuOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleLogout = () => {
        authService.logout();
        navigate('/');
        handleMenuClose();
    };

    const handleSubmenuToggle = (menuText) => {
        setOpenMenus(prev => ({
            ...prev,
            [menuText]: !prev[menuText]
        }));
    };

    const handleLogoError = () => {
        setLogoError(true);
    };

    const isActive = (path) => {
        if (!path) return false;
        return location.pathname === path || location.pathname.startsWith(path + '/');
    };

    const isParentActive = (item) => {
        if (item.path && isActive(item.path)) return true;
        if (item.children) {
            return item.children.some(child => isActive(child.path));
        }
        return false;
    };

    const getCurrentDrawerWidth = () => {
        return collapsed ? collapsedDrawerWidth : drawerWidth;
    };

    const renderMenuItem = (item, depth = 0) => {
        const hasChildren = item.children && item.children.length > 0;
        const active = isParentActive(item);
        const isOpen = openMenus[item.text];
        const paddingLeft = collapsed ? 0 : depth * 2;

        // Check if this is a leaf item (no children) and is active
        const isLeafActive = active && !hasChildren;
        // Check if this is a parent item with active children
        const isParentOfActive = active && hasChildren;

        if (collapsed && hasChildren) {
            return (
                <Tooltip 
                    key={item.text}
                    title={
                        <Box sx={{ p: 1 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                {item.text}
                            </Typography>
                            {item.children.map(child => (
                                <Typography key={child.text} variant="body2" sx={{ py: 0.5 }}>
                                    {child.text}
                                </Typography>
                            ))}
                        </Box>
                    }
                    placement="right"
                    arrow
                >
                    <ListItem
                        button
                        onClick={() => {
                            if (item.path) {
                                navigate(item.path);
                                setMobileOpen(false);
                            }
                        }}
                        sx={{
                            mx: 1,
                            mb: 0.5,
                            borderRadius: 2,
                            justifyContent: 'center',
                            minHeight: 48,
                            backgroundColor: active ? primaryColor : 'transparent',
                            '&:hover': {
                                backgroundColor: active ? primaryActiveHover : theme.palette.action.hover,
                            },
                        }}
                    >
                        <ListItemIcon 
                            sx={{ 
                                minWidth: 'auto',
                                justifyContent: 'center',
                                color: active ? '#fff' : 'white',
                            }}
                        >
                            {item.icon}
                        </ListItemIcon>
                    </ListItem>
                </Tooltip>
            );
        }

        return (
            <Box key={item.text}>
                <ListItem
                    button
                    onClick={() => {
                        if (hasChildren && !collapsed) {
                            handleSubmenuToggle(item.text);
                        } else if (item.path) {
                            navigate(item.path);
                            setMobileOpen(false);
                        }
                    }}
                    sx={{
                        mx: collapsed ? 1 : 2,
                        mb: 0.5,
                        borderRadius: 2,
                        justifyContent: collapsed ? 'center' : 'flex-start',
                        minHeight: 48,
                        pl: collapsed ? 0 : paddingLeft + 2,
                        pr: collapsed ? 0 : 2,
                        color:'white',
                        // Parent with active children: primary background, white text
                        backgroundColor: isParentOfActive ? primaryColor : (isLeafActive ? theme.palette.bodyBg.headerNavi : 'transparent'),
                        '&:hover': {
                            backgroundColor: isParentOfActive ? primaryActiveHover : theme.palette.primary.main,
                        },
                    }}
                >
                    <ListItemIcon 
                        sx={{ 
                            minWidth: collapsed ? 'auto' : 40,
                            justifyContent: 'center',
                            // Parent with active children: white icon
                            // Leaf active: primary color icon
                            // Others: white icon
                            color: isParentOfActive ? '#fff' : (isLeafActive ? primaryColor : 'white'),
                        }}
                    >
                        {item.icon}
                    </ListItemIcon>
                    {!collapsed && (
                        <>
                            <ListItemText
                            primary={
                                <Typography
                                    sx={{
                                        color: "white",
                                        fontWeight: active ? "bold" : "normal",
                                        fontSize: "0.9rem",
                                    }}
                                >
                                    {item.text}
                                </Typography>
                            }
                            secondary={
                                <Typography
                                    sx={{
                                        color: "rgba(255,255,255,0.7)",
                                        fontSize: "0.75rem",
                                    }}
                                >
                                    {item.description}
                                </Typography>
                            }
                        />
                            {hasChildren && (
                                isOpen ? (
                                    <ExpandLess sx={{ color: isParentOfActive ? '#fff' : 'white' }} />
                                ) : (
                                    <ExpandMore sx={{ color: isParentOfActive ? '#fff' : 'white' }} />
                                )
                            )}
                        </>
                    )}
                </ListItem>
                {!collapsed && hasChildren && (
                    <Collapse in={isOpen} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding>
                            {item.children.map(child => renderMenuItem(child, depth + 1))}
                        </List>
                    </Collapse>
                )}
            </Box>
        );
    };

    const drawer = (
        <Box sx={{  display: 'flex', color:'white', flexDirection: 'column', bgcolor: theme.palette.bodyBg.navi }}>
            {/* Header with Company Logo */}
            <Box sx={{ 
                p: collapsed ? 2 : 2.5, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: collapsed ? 'center' : 'space-between',
                borderBottom: 2,
                borderColor: primaryLight,
                minHeight: collapsed ? 70 : 'auto',
                background: theme.palette.bodyBg.headerNavi,
                position: 'relative',
                overflow: 'hidden'
            }}>
                {/* Background decoration */}
                <Box sx={{
                    position: 'absolute',
                    top: -20,
                    right: -20,
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    background: 'rgba(255,255,255,0.1)',
                    zIndex: 0
                }} />

                {!collapsed && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, position: 'relative', zIndex: 1 }}>
                        {/* Company Logo or Fallback */}
                        {loadingCompany ? (
                            <CircularProgress size={32} sx={{ color: 'white' }} />
                        ) : (companyInfo.has_logo && companyInfo.logo_url && !logoError) ? (
                            <Box sx={{
                                width: 40,
                                height: 40,
                                borderRadius: 2,
                                overflow: 'hidden',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: 'white',
                                flexShrink: 0,
                                boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                            }}>
                                <img
                                    src={companyInfo.logo_url}
                                    alt={`${companyInfo.name} Logo`}
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'contain',
                                        padding: '4px'
                                    }}
                                    onError={handleLogoError}
                                />
                            </Box>
                        ) : (
                            <Box sx={{
                                width: 40,
                                height: 40,
                                borderRadius: 2,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: theme.palette.primary.main,
                                flexShrink: 0,
                                boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                            }}>
                                <Typography sx={{ 
                                    color: 'white', 
                                    fontWeight: 'bold', 
                                    fontSize: '1.2rem',
                                    textTransform: 'uppercase'
                                }}>
                                    {companyInfo.name.charAt(0)}
                                </Typography>
                            </Box>
                        )}
                        <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#fff', lineHeight: 1.2, fontSize: '0.95rem' }}>
                                {companyInfo.name}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#fff', opacity: 0.9 }}>
                                RCMS
                            </Typography>
                        </Box>
                    </Box>
                )}
                
                {collapsed && (
                    <Box sx={{ position: 'relative', zIndex: 1,bgcolor: theme.palette.bodyBg.navi }}>
                        {loadingCompany ? (
                            <CircularProgress size={28} sx={{ color: 'white' }} />
                        ) : (companyInfo.has_logo && companyInfo.logo_url && !logoError) ? (
                            <Box sx={{
                                width: 36,
                                height: 36,
                                borderRadius: 2,
                                overflow: 'hidden',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: 'white',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                            }}>
                                <img
                                    src={companyInfo.logo_url}
                                    alt={`${companyInfo.name} Logo`}
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'contain',
                                        padding: '3px'
                                    }}
                                    onError={handleLogoError}
                                />
                            </Box>
                        ) : (
                            <Typography sx={{ 
                                color: 'white', 
                                fontWeight: 'bold', 
                                fontSize: '1.2rem',
                                textTransform: 'uppercase'
                            }}>
                                {companyInfo.name.charAt(0)}
                            </Typography>
                        )}
                    </Box>
                )}
                
                <IconButton 
                    onClick={handleToggleCollapse}
                    size="small"
                    sx={{ 
                        ml: collapsed ? 0 : 1,
                        backgroundColor: 'rgba(255,255,255,0.2)',
                        '&:hover': {
                            backgroundColor: 'rgba(255,255,255,0.3)',
                        },
                        color: '#fff',
                        position: 'relative',
                        zIndex: 1
                    }}
                >
                    {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
                </IconButton>
            </Box>

            {/* Menu Items */}
            <List sx={{ mt: 2, flex: 1, px: collapsed ? 1 : 0 }}>
                {menuItems.map(item => renderMenuItem(item))}
            </List>

            {/* User Info Section */}
            <Divider sx={{ my: 1 }} />
            <Box sx={{ p: collapsed ? 1 : 2 }}>
                <Tooltip title={collapsed ? (userName || 'User') : ''} placement="right">
                    <ListItem
                        button
                        onClick={handleMenuOpen}
                        sx={{
                            borderRadius: 2,
                            justifyContent: collapsed ? 'center' : 'flex-start',
                            minHeight: 56,
                            '&:hover': {
                                backgroundColor: theme.palette.primary.main,
                            },
                            bgcolor: '#F8F9FA'
                        }}
                    >
                        <ListItemIcon sx={{ minWidth: collapsed ? 'auto' : 40, justifyContent: 'center' }}>
                            <Avatar sx={{ width: 40, height: 40, bgcolor: theme.palette.primary.main }}>
                                {userName ? userName.charAt(0).toUpperCase() : <Person />}
                            </Avatar>
                        </ListItemIcon>
                        {!collapsed && (
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#2D3748' }}>
                                   {userName || 'User'}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#718096' }}>
                                    {userEmail || 'No email'}
                                </Typography>
                                <Chip 
                                    label="Admin" 
                                    size="small" 
                                    sx={{ 
                                        mt: 0.5, 
                                        height: 18, 
                                        fontSize: '0.6rem',
                                        bgcolor: theme.palette.primary.main,
                                        color: 'white',
                                        fontWeight: 'bold'
                                    }} 
                                />
                            </Box>
                        )}
                        {!collapsed && <ExpandMore fontSize="small" sx={{ color: '#2D3748' }} />}
                    </ListItem>
                </Tooltip>
            </Box>

            {/* User Menu */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                PaperProps={{
                    sx: {
                        mt: 1,
                        minWidth: 250,
                        borderRadius: 2,
                        boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                    }
                }}
            >
                <Box sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ width: 48, height: 48, bgcolor: primaryColor }}>
                            {userName ? userName.charAt(0).toUpperCase() : <Person />}
                        </Avatar>
                        <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: primaryColor }}>
                                {userName || 'User'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {userEmail || 'No email'}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
                <Divider />
                <MenuItem 
                    onClick={handleLogout} 
                    sx={{ 
                        color: '#d32f2f',
                        py: 1.5,
                        '&:hover': {
                            backgroundColor: '#ffebee',
                        }
                    }}
                >
                    <ListItemIcon>
                        <Logout fontSize="small" sx={{ color: '#d32f2f' }} />
                    </ListItemIcon>
                    <ListItemText primary="Logout" primaryTypographyProps={{ fontWeight: 'medium' }} />
                </MenuItem>
            </Menu>
        </Box>
    );

    return (
        <Box sx={{ display: 'flex' }}>
            <AppBar
                position="fixed"
                sx={{
                    width: { sm: `calc(100% - ${getCurrentDrawerWidth()}px)` },
                    ml: { sm: `${getCurrentDrawerWidth()}px` },
                    backgroundColor: theme.palette.primary.main,
                    color: 'white',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    transition: theme.transitions.create(['width', 'margin'], {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.leavingScreen,
                    }),
                }}
            >
                <Toolbar sx={{ minHeight: 64 }}>
                    <IconButton
                        color="inherit"
                        edge="start"
                        onClick={handleDrawerToggle}
                        sx={{ mr: 2, display: { sm: 'none' }, color: 'white' }}
                    >
                        <MenuIcon />
                    </IconButton>
                    
                    {/* AppBar Company Logo */}
                    <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 1.5, mr: 2 }}>
                        {loadingCompany ? (
                            <CircularProgress size={20} />
                        ) : (companyInfo.has_logo && companyInfo.logo_url && !logoError) ? (
                            <Box sx={{
                                width: 32,
                                height: 32,
                                borderRadius: 1.5,
                                overflow: 'hidden',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: 'white',
                                border: `1px solid ${theme.palette.divider}`
                            }}>
                                <img
                                    src={companyInfo.logo_url}
                                    alt={`${companyInfo.name} Logo`}
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'contain',
                                        padding: '2px'
                                    }}
                                    onError={handleLogoError}
                                />
                            </Box>
                        ) : (
                            <Box sx={{
                                width: 32,
                                height: 32,
                                borderRadius: 1.5,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: primaryColor,
                            }}>
                                <Typography sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.9rem' }}>
                                    {companyInfo.name.charAt(0)}
                                </Typography>
                            </Box>
                        )}
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'white' }}>
                            {companyInfo.name}
                        </Typography>
                    </Box>
                    
                    <Typography 
                        variant="h6" 
                        noWrap 
                        component="div" 
                        sx={{ 
                            fontWeight: 'bold', 
                            flexGrow: 1,
                            color: 'white',
                            fontSize: '1.1rem'
                        }}
                    >
                        Welcome back, {userName || 'User'}!
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 1 }}>
                            <Avatar sx={{ width: 35, height: 35, bgcolor: 'white' }}>
                                
                                <Typography variant="body2" sx={{fontWeight: 'bold', color:  theme.palette.primary.main }}>
                                   {userName ? userName.charAt(0).toUpperCase() : <Person />}
                                </Typography>
                            </Avatar>
                            <Box>
                                <Typography variant="body2" sx={{ fontWeight: 'medium', color: 'white' }}>
                                   {userName || 'User'}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'white' }}>
                                    RCM User
                                </Typography>
                            </Box>
                        </Box>
                        <Tooltip title="Logout">
                            <IconButton 
                                onClick={handleLogout} 
                                size="small"
                                sx={{ 
                                    color: '#d32f2f',
                                    '&:hover': {
                                        backgroundColor: '#ffebee',
                                    }
                                }}
                            >
                                <Logout />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Toolbar>
            </AppBar>

            {/* Mobile Drawer */}
            <Box
                component="nav"
                sx={{ width: { sm: getCurrentDrawerWidth() }, flexShrink: { sm: 0 } }}
            >
                <Drawer
                    variant="temporary"
                    open={mobileOpen}
                    onClose={handleDrawerToggle}
                    ModalProps={{ keepMounted: true }}
                    sx={{
                        display: { xs: 'block', sm: 'none' },
                        '& .MuiDrawer-paper': { 
                            width: drawerWidth,
                            boxSizing: 'border-box',
                            borderRight: 'none',
                        },
                    }}
                >
                    {drawer}
                </Drawer>
                
                {/* Desktop Drawer */}
                <Drawer
                    variant="permanent"
                    sx={{
                        display: { xs: 'none', sm: 'block' },
                        '& .MuiDrawer-paper': { 
                            width: getCurrentDrawerWidth(),
                            boxSizing: 'border-box',
                            transition: theme.transitions.create('width', {
                                easing: theme.transitions.easing.sharp,
                                duration: theme.transitions.duration.enteringScreen,
                            }),
                            overflowX: 'hidden',
                            borderRight: `1px solid ${theme.palette.divider}`,
                            boxShadow: '2px 0 8px rgba(0,0,0,0.05)',
                        },
                    }}
                    open
                >
                    {drawer}
                </Drawer>
            </Box>

            {/* Main Content */}
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    p: 3,
                    width: { sm: `calc(100% - ${getCurrentDrawerWidth()}px)` },
                    mt: 8,
                    backgroundColor: theme.palette.bodyBg.main,
                    minHeight: '100vh',
                    transition: theme.transitions.create(['width', 'margin'], {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.enteringScreen,
                    }),
                }}
            >
                {children}
            </Box>
        </Box>
    );
};

export default Layout;